import { GameEventSchema, directorTeamName, type CallLock, type CallOffer, type DirectorLanguage, type DirectorResponse, type GameEvent, type Resolution, type RoomSnapshot } from '@clutch/core';
import { friendVersion, type FriendVersion } from './perspective';

/** Only fields needed by this view; current final metadata/presence never enter a story. */
export type CatchUpEvidence = {
  roomId: string;
  gameId: string;
  cutoff: number;
  state: Pick<RoomSnapshot['state'], 'period' | 'clockSec' | 'homeScore' | 'awayScore' | 'status'>;
  live: boolean;
  plays: GameEvent[];
  call?: CallOffer;
  locks: CallLock[];
  resolution?: Resolution;
};
export type BoundCatchUp = { director: DirectorResponse; plays: GameEvent[]; live: boolean; friend?: FriendVersion };

export function captureCatchUp(snapshot: RoomSnapshot, plays: readonly GameEvent[]): CatchUpEvidence {
  const cutoff = snapshot.state.asOfSeq;
  if (!Number.isInteger(cutoff) || cutoff < -1 || snapshot.gameId !== snapshot.meta.gameId || snapshot.state.gameId !== snapshot.gameId) throw new Error('Invalid catch-up snapshot');
  const bySeq = new Map<number, GameEvent>();
  const ambiguous = new Set<number>();
  for (const raw of plays) {
    if (raw.seq > cutoff) continue;
    const parsed = GameEventSchema.safeParse(raw);
    if (!parsed.success || raw.gameId !== snapshot.gameId || !raw.eventId.startsWith(`${snapshot.gameId}:`) || raw.clockSec > (raw.period <= 4 ? 720 : 300)) continue;
    if (bySeq.has(raw.seq) && JSON.stringify(bySeq.get(raw.seq)) !== JSON.stringify(parsed.data)) ambiguous.add(raw.seq);
    bySeq.set(raw.seq, parsed.data);
  }
  const ids = new Map<string, number>();
  for (const play of bySeq.values()) ids.set(play.eventId, (ids.get(play.eventId) ?? 0) + 1);
  const bounded = [...bySeq.values()].filter((e) => !ambiguous.has(e.seq) && ids.get(e.eventId) === 1).sort((a, b) => a.seq - b.seq);
  return structuredClone({
    roomId: snapshot.roomId, gameId: snapshot.gameId, cutoff,
    state: { period: snapshot.state.period, clockSec: snapshot.state.clockSec, homeScore: snapshot.state.homeScore, awayScore: snapshot.state.awayScore, status: snapshot.state.status },
    live: snapshot.feed?.mode === 'live', plays: bounded,
    ...(snapshot.call ? { call: snapshot.call } : {}), locks: snapshot.locks,
    ...(snapshot.resolution ? { resolution: snapshot.resolution } : {}),
  });
}

const sameScoreClock = (a: { period: number; clockSec: number; homeScore: number; awayScore: number }, b: typeof a) =>
  a.period === b.period && a.clockSec === b.clockSec && a.homeScore === b.homeScore && a.awayScore === b.awayScore;

/** A room settlement alone cannot establish another fan's outcome in a historical view. */
function boundedFriend(director: DirectorResponse, evidence: CatchUpEvidence, plays: readonly GameEvent[], fanId: string): FriendVersion | undefined {
  const { call, resolution } = evidence;
  if (director.asOf.status !== 'final' || !call || !resolution || call.status !== 'resolved'
    || call.roomId !== director.roomId || call.gameId !== director.game.gameId || call.callId !== resolution.callId
    || (director.call && director.call.callId !== call.callId) || !['home', 'away'].includes(call.subject)) return;
  const final = plays.find((p) => p.eventId === resolution.decidedByEventId);
  if (!final || final.eventId !== director.asOf.eventId || final.type !== 'game_end' || final.period < 4 || final.clockSec !== 0
    || final.seq > director.asOf.seq || !sameScoreClock(final, director.asOf)
    || resolution.finalHome !== final.homeScore || resolution.finalAway !== final.awayScore || final.homeScore === final.awayScore) return;
  const outcome = (final.homeScore > final.awayScore ? 'home' : 'away') === call.subject ? 'yes' : 'no';
  if (resolution.outcome !== outcome) return;
  const at = (seq: number) => seq === -1 ? { period: 1, clockSec: 720 } : plays.find((p) => p.seq === seq);
  const offered = at(call.offeredAtSeq);
  if (!offered || call.offeredAtSeq > director.asOf.seq || offered.period !== call.offeredAt.period || offered.clockSec !== call.offeredAt.clockSec) return;
  const locks = evidence.locks.filter((lock) => {
    const anchored = at(lock.lockedAtSeq);
    return lock.callId === call.callId && lock.status === 'confirmed' && lock.lockedAtSeq >= call.offeredAtSeq
      && lock.lockedAtSeq <= director.asOf.seq && anchored?.period === lock.lockedAt.period && anchored.clockSec === lock.lockedAt.clockSec
      && ['yes', 'no'].includes(lock.choice) && Number.isFinite(lock.pChoiceAtLock) && lock.pChoiceAtLock >= 0 && lock.pChoiceAtLock <= 1
      && Number.isFinite(Date.parse(lock.serverLockedAt));
  });
  const results = resolution.results.flatMap((result) => {
    const matches = locks.filter((lock) => lock.fanId === result.fanId);
    if (matches.length !== 1 || resolution.results.filter((r) => r.fanId === result.fanId).length !== 1) return [];
    const lock = matches[0]!;
    return lock.choice === result.choice && result.correct === (result.choice === outcome)
      ? [{ ...result, displayName: lock.displayName }] : [];
  });
  return friendVersion({
    resolution: { outcome, results }, locks,
    call: { yesLabel: directorTeamName(director.game[call.subject].tricode, director.language), noLabel: directorTeamName(director.game[call.subject === 'home' ? 'away' : 'home'].tricode, director.language) },
    myFanId: fanId, ...(director.call ? { myChoice: director.call.choice } : {}), language: director.language,
  });
}

export function bindCatchUp(director: DirectorResponse, evidence: CatchUpEvidence, language: DirectorLanguage, fanId: string): BoundCatchUp {
  const exactHead = director.asOf.seq === evidence.cutoff && sameScoreClock(director.asOf, evidence.state) && director.asOf.status === evidence.state.status;
  const frozenPlay = evidence.plays.find((p) => p.seq === director.asOf.seq && p.eventId === director.asOf.eventId && sameScoreClock(p, director.asOf));
  const frozenStatus = frozenPlay && frozenPlay.type === 'game_end' && frozenPlay.period >= 4 && frozenPlay.clockSec === 0 ? 'final' : 'live';
  // A saved take can be an earlier play of this same live game. Hide everything after that moment.
  const frozen = !exactHead && !!frozenPlay && director.asOf.seq >= 0 && director.asOf.seq < evidence.cutoff && director.asOf.status === frozenStatus;
  const view = frozen ? evidence.plays.filter((p) => p.seq <= director.asOf.seq) : evidence.plays;
  if (director.language !== language || director.roomId !== evidence.roomId || director.game.gameId !== evidence.gameId
    || (!exactHead && !frozen)
    || director.moments.some((m) => m.seq > director.asOf.seq)
    || (director.call && (director.call.lockedAtSeq > director.asOf.seq || (director.asOf.status !== 'final' && director.call.result !== 'pending')))) {
    throw new Error('Catch-up response does not match its snapshot');
  }
  const latest = view.find((p) => p.seq === director.asOf.seq);
  // If the separate plays endpoint is incomplete or was corrected during capture, omit visual
  // history/derived lead claims instead of combining two versions of the game.
  const matching = !!latest && latest.eventId === director.asOf.eventId && sameScoreClock(latest, director.asOf)
    && director.moments.every((m) => {
      const at = view.find((p) => p.seq === m.seq);
      return !!at && sameScoreClock(at, m) && m.eventIds.every((id) => view.some((p) => p.eventId === id));
    });
  const plays = matching ? view : [];
  const friend = boundedFriend(director, evidence, plays, fanId);
  return structuredClone({ director, plays, live: evidence.live, ...(friend ? { friend } : {}) });
}

/**
 * A language change translates one frozen view; it never asks for the newly advanced room.
 * A `variant` (the Director sheet's take identity: style, energy, length, lens) is another view
 * of the same frozen cutoff, cached separately so remixes and perspective flips stay instant.
 */
export class CatchUpSession {
  private readonly evidence: CatchUpEvidence;
  private readonly cache = new Map<string, BoundCatchUp>();
  private readonly pending = new Map<string, Promise<BoundCatchUp>>();
  private revision = 0;
  constructor(evidence: CatchUpEvidence, private readonly fanId: string,
    private readonly request: (language: DirectorLanguage, asOfSeq: number, variant?: string) => Promise<DirectorResponse>) {
    this.evidence = structuredClone(evidence);
  }
  cancel() { this.revision++; }
  async select(language: DirectorLanguage, variant = ''): Promise<BoundCatchUp | undefined> {
    const revision = ++this.revision;
    const key = variant ? `${language}#${variant}` : language;
    const hit = this.cache.get(key);
    if (hit) {
      await Promise.resolve();
      return this.revision === revision ? structuredClone(hit) : undefined;
    }
    let pending = this.pending.get(key);
    if (!pending) {
      pending = (variant ? this.request(language, this.evidence.cutoff, variant) : this.request(language, this.evidence.cutoff)).then((director) => {
        const bound = bindCatchUp(director, this.evidence, language, this.fanId);
        this.cache.set(key, bound);
        return bound;
      }).finally(() => { this.pending.delete(key); });
      this.pending.set(key, pending);
    }
    try {
      const result = await pending;
      return this.revision === revision ? structuredClone(result) : undefined;
    } catch (error) {
      if (this.revision !== revision) return undefined;
      throw error;
    }
  }
}
